import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react'
import toast from 'react-hot-toast'
import talentFavoritesService from '../services/talentFavoritesService'
import { useAuth } from '../hooks/useAuth'
import { formatApiError } from '../utils/formatApiError'

/**
 * Личное избранное талантов.
 *
 * Все отметки пользователя грузятся ОДНИМ запросом при монтировании и живут
 * в Map<talent_id, Set<list_id>>. Благодаря этому звезда в строке таблицы
 * не делает запрос на строку — при 200 строках это 0 запросов вместо 200.
 * Если избранного накопилось слишком много, дамп заменяется точечным lookup
 * по id текущей страницы (см. lookupTalents).
 */
const FAVORITES_DUMP_LIMIT = 2000

const FavoritesContext = createContext(null)

export const useFavorites = () => {
  const ctx = useContext(FavoritesContext)
  if (!ctx) {
    throw new Error('useFavorites must be used within FavoritesProvider')
  }
  return ctx
}

export const FavoritesProvider = ({ children }) => {
  const { user } = useAuth()
  const userId = user?.id || null

  const [lists, setLists] = useState([])
  const [byTalent, setByTalent] = useState(() => new Map())
  const [pendingIds, setPendingIds] = useState(() => new Set())
  const [loaded, setLoaded] = useState(false)
  // Актуальный снимок byTalent, читаемый из колбэков без пересоздания функций
  const byTalentRef = useRef(byTalent)
  // true, когда отметок слишком много для полного дампа — тогда данные
  // догружаются постранично через lookupTalents
  const partialRef = useRef(false)

  const applyRows = useCallback((rows) => {
    const map = new Map()
    for (const row of rows) {
      const talentId = Number(row.talent_id)
      if (!map.has(talentId)) map.set(talentId, new Set())
      map.get(talentId).add(Number(row.list_id))
    }
    return map
  }, [])

  // Как и byTalentRef — чтобы toggleFavorite не пересоздавался на каждое
  // изменение списков и не мог быть захвачен со старым снимком
  const listsRef = useRef(lists)
  const commitLists = useCallback((nextLists) => {
    listsRef.current = nextLists
    setLists(nextLists)
  }, [])

  const refreshLists = useCallback(async () => {
    if (!userId) return
    try {
      const response = await talentFavoritesService.listLists()
      commitLists(response?.data || [])
    } catch (error) {
      console.error('Failed to load favorite lists:', error)
    }
  }, [userId, commitLists])

  useEffect(() => {
    if (!userId) {
      commitLists([])
      byTalentRef.current = new Map()
      setByTalent(byTalentRef.current)
      setLoaded(false)
      return
    }

    let cancelled = false
    const load = async () => {
      try {
        const [listsResponse, flatResponse] = await Promise.all([
          talentFavoritesService.listLists(),
          talentFavoritesService.myFavoritesFlat(),
        ])
        if (cancelled) return

        const rows = flatResponse?.data || []
        partialRef.current = rows.length > FAVORITES_DUMP_LIMIT
        commitLists(listsResponse?.data || [])
        byTalentRef.current = applyRows(rows)
        setByTalent(byTalentRef.current)
      } catch (error) {
        if (!cancelled) console.error('Failed to load favorites:', error)
      } finally {
        if (!cancelled) setLoaded(true)
      }
    }

    load()
    return () => { cancelled = true }
  }, [userId, applyRows])

  /**
   * Догрузка отметок по id текущей страницы. Нужна только когда избранного
   * столько, что полный дамп нецелесообразен.
   */
  const lookupTalents = useCallback(async (talentIds) => {
    if (!userId || !partialRef.current || !talentIds?.length) return
    try {
      const response = await talentFavoritesService.lookup(talentIds)
      const data = response?.data || {}
      const next = new Map(byTalentRef.current)
      for (const id of talentIds) next.delete(Number(id))
      for (const [talentId, listIds] of Object.entries(data)) {
        next.set(Number(talentId), new Set(listIds.map(Number)))
      }
      byTalentRef.current = next
      setByTalent(next)
    } catch (error) {
      console.error('Failed to look up favorites:', error)
    }
  }, [userId])

  const listIdsOf = useCallback((talentId) => byTalent.get(Number(talentId)) || null, [byTalent])
  const isFavorite = useCallback((talentId) => (byTalent.get(Number(talentId))?.size || 0) > 0, [byTalent])
  const isPending = useCallback((talentId) => pendingIds.has(Number(talentId)), [pendingIds])

  const markPending = useCallback((talentId, pending) => {
    setPendingIds(prev => {
      const next = new Set(prev)
      if (pending) next.add(Number(talentId))
      else next.delete(Number(talentId))
      return next
    })
  }, [])

  /**
   * Источник правды — byTalentRef, состояние лишь зеркалит его для рендера.
   *
   * Так сделано намеренно: раньше writeSet и откат читали byTalent из замыкания,
   * поэтому setListsFor пересоздавался на каждое изменение избранного. Функция,
   * захваченная где-то снаружи дерева компонента (например, в колбэке тоста
   * «Undo»), оказывалась со снимком состояния из прошлого рендера и откатывалась
   * не туда. Через ref все колбэки стабильны на всю сессию и всегда видят
   * актуальные данные.
   */
  const commitMap = useCallback((nextMap) => {
    byTalentRef.current = nextMap
    setByTalent(nextMap)
  }, [])

  const writeSet = useCallback((talentId, listIds) => {
    const next = new Map(byTalentRef.current)
    const set = new Set((listIds || []).map(Number))
    if (set.size === 0) next.delete(Number(talentId))
    else next.set(Number(talentId), set)
    commitMap(next)
  }, [commitMap])

  const readSet = useCallback((talentId) => {
    const set = byTalentRef.current.get(Number(talentId))
    return set ? [...set] : []
  }, [])

  /**
   * Клик по звезде: без listId работает со списком по умолчанию.
   * Возвращает true, если сервер подтвердил изменение.
   */
  const toggleFavorite = useCallback(async (talentId, listId = null) => {
    if (!userId) return false
    const id = Number(talentId)
    const previous = readSet(id)

    // Оптимистично переключаем ровно один список — тот же, что переключит сервер.
    // Без listId это список по умолчанию: талант может лежать и в других списках,
    // тогда звезда останется закрашенной.
    const optimistic = new Set(previous)
    const targetListId = listId ? Number(listId) : (listsRef.current.find(l => l.is_default)?.id ?? null)
    if (targetListId === null) {
      // дефолтного списка ещё нет — он создастся на сервере, реальный id придёт в ответе
      optimistic.add(-1)
    } else if (optimistic.has(targetListId)) {
      optimistic.delete(targetListId)
    } else {
      optimistic.add(targetListId)
    }
    writeSet(id, [...optimistic])
    markPending(id, true)

    try {
      const response = await talentFavoritesService.toggle(id, listId)
      writeSet(id, response?.data?.list_ids || [])
      // первый клик мог создать список по умолчанию
      if (response?.data?.list && !listsRef.current.some(l => l.id === response.data.list.id)) {
        refreshLists()
      }
      return true
    } catch (error) {
      writeSet(id, previous)
      console.error('Failed to toggle favorite:', error)
      toast.error(formatApiError(error, 'Failed to update favorites'))
      return false
    } finally {
      markPending(id, false)
    }
  }, [userId, readSet, writeSet, markPending, refreshLists])

  /**
   * Талант оказывается ровно в переданных списках.
   * Возвращает true, если сервер подтвердил изменение: вызывающий код
   * (например, «Undo») обязан отличать успех от тихого отката.
   */
  const setListsFor = useCallback(async (talentId, listIds) => {
    if (!userId) {
      console.error('setListsFor called without an authenticated user')
      return false
    }
    const id = Number(talentId)
    const previous = readSet(id)

    writeSet(id, listIds)
    markPending(id, true)

    try {
      const response = await talentFavoritesService.setLists(id, listIds)
      writeSet(id, response?.data?.list_ids || [])
      return true
    } catch (error) {
      writeSet(id, previous)
      console.error('Failed to set favorite lists:', error)
      toast.error(formatApiError(error, 'Failed to update favorites'))
      return false
    } finally {
      markPending(id, false)
    }
  }, [userId, readSet, writeSet, markPending])

  const createList = useCallback(async (name) => {
    const response = await talentFavoritesService.createList({ name })
    await refreshLists()
    return response?.data || null
  }, [refreshLists])

  const updateList = useCallback(async (id, payload) => {
    const response = await talentFavoritesService.updateList(id, payload)
    await refreshLists()
    return response?.data || null
  }, [refreshLists])

  const deleteList = useCallback(async (id) => {
    await talentFavoritesService.deleteList(id)
    // удаление списка каскадом снимает его отметки
    const next = new Map()
    for (const [talentId, set] of byTalentRef.current.entries()) {
      const kept = new Set([...set].filter(listId => listId !== Number(id)))
      if (kept.size > 0) next.set(talentId, kept)
    }
    commitMap(next)
    await refreshLists()
  }, [refreshLists, commitMap])

  const value = useMemo(() => ({
    lists,
    loaded,
    isFavorite,
    listIdsOf,
    isPending,
    toggleFavorite,
    setListsFor,
    lookupTalents,
    refreshLists,
    createList,
    updateList,
    deleteList,
  }), [lists, loaded, isFavorite, listIdsOf, isPending, toggleFavorite, setListsFor,
    lookupTalents, refreshLists, createList, updateList, deleteList])

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  )
}

export default FavoritesContext
